import { useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { X, Sparkles, ShieldCheck, History } from "lucide-react";

/**
 * Modal shown to anonymous shoppers when they try to claim a personalised CYN- code.
 * User's decision: force sign-in / register — no guest claims.
 */
export default function SignInPromptModal({ open, onClose, storeName }) {
  const location = useLocation();

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const nextState = { from: location.pathname + location.search };

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      data-testid="signin-prompt-modal"
      aria-modal="true"
      role="dialog"
    >
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-md"
        onClick={onClose}
      />
      <div className="glass-strong rounded-[28px] max-w-md w-full p-8 relative z-10 fade-up">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-white/70 text-slate-500"
          aria-label="Close"
          data-testid="signin-prompt-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="pf-chip pf-chip-cyan w-fit">
          <Sparkles className="w-3 h-3" /> Personalised code
        </div>
        <h2 className="font-display text-3xl font-medium tracking-tight mt-3 text-slate-900 leading-tight">
          Sign in to claim your unique code
        </h2>
        <p className="text-sm text-slate-600 mt-3 leading-relaxed">
          Every Cyannect coupon{storeName ? ` from ${storeName}` : ""} is generated one-per-shopper.
          To save it to your account and reuse it later, sign in or create a free account.
        </p>

        <div className="mt-6 space-y-2">
          <div className="flex items-start gap-3 text-sm text-slate-700">
            <ShieldCheck className="w-4 h-4 mt-0.5 text-cyan-600 shrink-0" />
            <span>Codes are tied to your account so merchants can honour them at checkout.</span>
          </div>
          <div className="flex items-start gap-3 text-sm text-slate-700">
            <History className="w-4 h-4 mt-0.5 text-cyan-600 shrink-0" />
            <span>Access your full claim history any time from your dashboard.</span>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-3">
          <Link
            to="/login"
            state={nextState}
            className="pf-btn-primary py-3 rounded-full font-semibold text-sm text-center"
            data-testid="signin-prompt-login"
          >
            Sign in
          </Link>
          <Link
            to="/register"
            state={nextState}
            className="pf-btn-glass py-3 rounded-full font-semibold text-sm text-center"
            data-testid="signin-prompt-register"
          >
            Create account
          </Link>
        </div>

        <p className="text-[11px] text-slate-500 mt-5 text-center">
          Takes less than 20 seconds. No credit card.
        </p>
      </div>
    </div>,
    document.body
  );
}
